import React, { useState, useEffect } from 'react';
import { AlertCircle, PlusCircle, RefreshCw, Info, ShoppingCart } from 'lucide-react';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { apiEndpoints } from '../../utils/api';
import { format } from 'date-fns';
import { PurchaseQuotaTopupModal } from '../billing/PurchaseQuotaTopupModal';
import { Spinner } from '../../design-system';
import { isUnlimitedQuota } from '../../utils/quota';

interface SubscriptionTier {
  id: string;
  name: string;
  display_name: string;
  metadata?: {
    quota_prs_per_month?: number;
    quota_sast_scans_per_month?: number;
    quota_deep_scans_per_month?: number;
  };
}

interface CurrentSubscription {
  tier_name: string;
  status: string;
  current_period_end: string;
}

interface QuotaBalance {
  period_start: string;
  period_end: string;
  plan_prs: number;
  topup_prs: number;
  used_prs: number;
  remaining_prs: number;
  plan_sast_scans: number;
  topup_sast_scans: number;
  used_sast_scans: number;
  remaining_sast_scans: number;
  plan_deep_scans: number;
  topup_deep_scans: number;
  used_deep_scans: number;
  remaining_deep_scans: number;
}

interface QuotaCardProps {
  label: string;
  description: string;
  remaining: number;
  total: number;
  used: number;
  planAmount: number;
  topupAmount: number;
}

const QuotaCard: React.FC<QuotaCardProps> = ({
  label,
  description,
  remaining,
  total,
  used,
  planAmount,
  topupAmount,
}) => {
  const unlimited = isUnlimitedQuota(planAmount);
  const usagePercentage = total > 0 && !unlimited ? Math.min((used / total) * 100, 100) : 0;
  const isLow = total > 0 && !unlimited && remaining <= Math.ceil(total * 0.2);

  return (
    <div className="bg-ui-background-tertiary rounded-lg p-5 border border-ui-border-primary">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="font-semibold text-ui-text-primary">{label}</h4>
          <p className="text-xs text-ui-text-tertiary mt-0.5">{description}</p>
        </div>
      </div>

      <div className="mb-3">
        <div className="flex items-baseline gap-1">
          <span className={`text-2xl font-bold ${isLow ? 'text-amber-600 dark:text-amber-400' : 'text-ui-text-primary'}`}>
            {unlimited ? 'Unlimited' : remaining}
          </span>
          {!unlimited && <span className="text-ui-text-tertiary">/ {total} remaining</span>}
        </div>
      </div>

      <div className="mb-3">
        <div className="h-2 bg-ui-background-tertiary rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              isLow ? 'bg-amber-500' : 'bg-brand-primary'
            }`}
            style={{ width: `${usagePercentage}%` }}
          />
        </div>
        <p className="text-xs text-ui-text-tertiary mt-1">
          {used} used this period
        </p>
      </div>

      <div className="flex items-center gap-4 text-xs text-ui-text-secondary pt-2 border-t border-ui-border-primary">
        <span>Plan: {unlimited ? 'Unlimited' : planAmount}</span>
        <span>Top-ups: {topupAmount}</span>
      </div>
    </div>
  );
};

const BUNDLE_ELIGIBLE_TIERS = ['team'];

const TopUpsSection: React.FC = () => {
  const api = useAuthenticatedApi();
  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<CurrentSubscription | null>(null);
  const [quotaBalance, setQuotaBalance] = useState<QuotaBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  useEffect(() => {
    if (api) {
      fetchData();
    }
  }, [api]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [tiersResponse, subscriptionResponse] = await Promise.all([
        api.get(apiEndpoints.billing.subscriptionTiers),
        api.get(apiEndpoints.billing.subscription),
      ]);

      setTiers(tiersResponse || []);
      setCurrentSubscription(subscriptionResponse);

      if (subscriptionResponse) {
        try {
          const quotaResponse = await api.get(apiEndpoints.billing.quotaBalance);
          setQuotaBalance(quotaResponse || null);
        } catch {
          setQuotaBalance(null);
        }
      }
    } catch {
      setError('Failed to load top-up data');
    } finally {
      setLoading(false);
    }
  };

  const currentTier = currentSubscription
    ? tiers.find((tier) => tier.name === currentSubscription.tier_name)
    : undefined;

  const bundleContents = currentTier?.metadata
    ? {
        prs: currentTier.metadata.quota_prs_per_month || 0,
        sast_scans: currentTier.metadata.quota_sast_scans_per_month || 0,
        deep_scans: currentTier.metadata.quota_deep_scans_per_month || 0,
      }
    : undefined;

  const canPurchaseBundle = currentSubscription &&
    BUNDLE_ELIGIBLE_TIERS.includes(currentSubscription.tier_name);

  const quotaTotals = quotaBalance
    ? {
        prs: quotaBalance.plan_prs + quotaBalance.topup_prs,
        sast: quotaBalance.plan_sast_scans + quotaBalance.topup_sast_scans,
        deep: quotaBalance.plan_deep_scans + quotaBalance.topup_deep_scans,
      }
    : null;

  const hasQuotaLimits = quotaTotals
    ? quotaTotals.prs > 0 || quotaTotals.sast > 0 || quotaTotals.deep > 0
    : false;

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-8">
          <Spinner size={32} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (!currentSubscription) {
    return (
      <div className="p-6">
        <h2 className="text-2xl font-semibold text-ui-text-primary mb-6">Top-Ups</h2>
        <div className="bg-ui-background-tertiary border border-ui-border-primary rounded-lg p-8 text-center">
          <Info size={48} className="mx-auto text-ui-text-tertiary mb-4" />
          <h3 className="text-lg font-medium text-ui-text-primary mb-2">
            No Active Subscription
          </h3>
          <p className="text-ui-text-secondary max-w-md mx-auto">
            You need an active subscription to purchase quota top-ups. Visit the Plan section to subscribe to a plan.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6">
        <div className="min-w-0">
          <h2 className="text-2xl font-semibold text-ui-text-primary">Top-Ups</h2>
          <p className="text-sm text-ui-text-secondary mt-1">
            {canPurchaseBundle
              ? 'Purchase additional quota when you need more capacity'
              : 'Quota bundles are available on Team plans'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          {canPurchaseBundle && (
            <button
              onClick={() => setShowPurchaseModal(true)}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-brand-primary hover:bg-brand-primaryDark text-black rounded-lg transition-colors shadow-sm"
            >
              <ShoppingCart size={16} />
              Buy Quota Bundle - $99
            </button>
          )}
          <button
            onClick={fetchData}
            disabled={loading}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-3 py-1.5 text-sm font-medium text-ui-text-secondary hover:text-ui-text-primary transition-colors flex-shrink-0"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {quotaBalance && quotaTotals && hasQuotaLimits ? (
        <>
          <div className="mb-6 p-4 bg-brand-primary/5 border border-brand-primary/20 rounded-lg">
            <div className="flex items-center gap-2 text-sm text-ui-text-secondary">
              <Info size={16} className="text-brand-primary" />
              <span>
                Current billing period: {format(new Date(quotaBalance.period_start), 'MMM d')} -{' '}
                {format(new Date(quotaBalance.period_end), 'MMM d, yyyy')}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <QuotaCard
              label="Fix PRs"
              description="Pull requests generated from fixes"
              remaining={quotaBalance.remaining_prs}
              total={quotaTotals.prs}
              used={quotaBalance.used_prs}
              planAmount={quotaBalance.plan_prs}
              topupAmount={quotaBalance.topup_prs}
            />
            <QuotaCard
              label="SAST Scans"
              description="Secrets + Semgrep scans"
              remaining={quotaBalance.remaining_sast_scans}
              total={quotaTotals.sast}
              used={quotaBalance.used_sast_scans}
              planAmount={quotaBalance.plan_sast_scans}
              topupAmount={quotaBalance.topup_sast_scans}
            />
            <QuotaCard
              label="Deep AI Scans"
              description="AI and Deep AI scans"
              remaining={quotaBalance.remaining_deep_scans}
              total={quotaTotals.deep}
              used={quotaBalance.used_deep_scans}
              planAmount={quotaBalance.plan_deep_scans}
              topupAmount={quotaBalance.topup_deep_scans}
            />
          </div>

          {canPurchaseBundle ? (
            <div className="bg-ui-background-tertiary border border-ui-border-primary rounded-lg p-5">
              <h3 className="font-semibold text-ui-text-primary mb-3">How Top-Ups Work</h3>
              <ul className="space-y-2 text-sm text-ui-text-secondary">
                <li className="flex items-start gap-2">
                  <PlusCircle size={16} className="text-brand-primary mt-0.5 flex-shrink-0" />
                  <span>Purchase a quota bundle for $99 to get the same quotas as your monthly plan</span>
                </li>
                <li className="flex items-start gap-2">
                  <PlusCircle size={16} className="text-brand-primary mt-0.5 flex-shrink-0" />
                  <span>Top-ups add to your quota immediately after payment</span>
                </li>
                <li className="flex items-start gap-2">
                  <PlusCircle size={16} className="text-brand-primary mt-0.5 flex-shrink-0" />
                  <span>Plan quota is consumed first, then top-up quota</span>
                </li>
              </ul>
            </div>
          ) : (
            <div className="bg-ui-background-tertiary border border-ui-border-primary rounded-lg p-5">
              <div className="flex items-start gap-2 text-sm text-ui-text-secondary">
                <Info size={16} className="text-brand-primary mt-0.5 flex-shrink-0" />
                <span>Upgrade to Team to purchase additional scan and PR quota bundles.</span>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="bg-ui-background-tertiary border border-ui-border-primary rounded-lg p-8 text-center">
          <Info size={48} className="mx-auto text-ui-text-tertiary mb-4" />
          <h3 className="text-lg font-medium text-ui-text-primary mb-2">
            No Quota Limits
          </h3>
          <p className="text-ui-text-secondary max-w-md mx-auto">
            Your current plan doesn't have quota limits. You have unlimited access to all features.
          </p>
        </div>
      )}

      <PurchaseQuotaTopupModal
        show={showPurchaseModal}
        onHide={() => setShowPurchaseModal(false)}
        onSuccess={fetchData}
        bundleContents={bundleContents}
        tierDisplayName={currentTier?.display_name}
      />
    </div>
  );
};

export default TopUpsSection;
